package dev.lingvo.site.components.studio.editor

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.tabIndex
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import dev.lingvo.shared.screenshots.doc.BackgroundLayer
import dev.lingvo.shared.screenshots.doc.GroupLayer
import dev.lingvo.shared.screenshots.doc.Layer
import dev.lingvo.site.ds.LIconAction
import dev.lingvo.site.ds.LEyeIcon
import dev.lingvo.site.ds.LLockIcon
import dev.lingvo.site.ds.LTextField
import dev.lingvo.shared.screenshots.doc.DeviceLayer
import dev.lingvo.shared.screenshots.doc.ImageLayer
import dev.lingvo.shared.screenshots.doc.TextLayer
import dev.lingvo.site.ds.LImageIcon
import dev.lingvo.site.ds.LLayersIcon
import dev.lingvo.site.ds.LSmartphoneIcon
import dev.lingvo.site.ds.LSquareIcon
import dev.lingvo.site.ds.LTypeIcon
import dev.lingvo.site.ds.Space
import org.jetbrains.compose.web.dom.Span
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import kotlinx.browser.document
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

private data class TreeRow(val layer: Layer, val level: Int, val parent: String?)

/** The tree is the canvas's structural mirror and supports every selection without pointer input. */
@Composable
internal fun LayersPanel(state: StudioUiState, dispatch: (StudioIntent) -> Unit) {
    var expanded by remember { mutableStateOf(emptySet<String>()) }
    var focusId by remember(state.page.id) { mutableStateOf<String?>(null) }
    var renameId by remember { mutableStateOf<String?>(null) }
    var rename by remember { mutableStateOf("") }
    var dragging by remember { mutableStateOf<String?>(null) }
    var dropTarget by remember { mutableStateOf<String?>(null) }
    var dropAfter by remember { mutableStateOf(false) }
    fun rows(layers: List<Layer>, level: Int = 1, parent: String? = null): List<TreeRow> = layers.asReversed().flatMap {
        listOf(TreeRow(it, level, parent)) + if (it is GroupLayer && it.base.id in expanded) rows(it.children, level + 1, it.base.id) else emptyList()
    }
    LaunchedEffect(renameId) {
        renameId?.let { id -> (document.getElementById("studio-layer-$id")?.querySelector("input") as? HTMLElement)?.focus() }
    }
    val visible = rows(state.layers)
    val active = focusId?.takeIf { id -> visible.any { it.layer.base.id == id } }
        ?: visible.firstOrNull { it.layer.base.id in state.selection }?.layer?.base?.id ?: visible.firstOrNull()?.layer?.base?.id
    fun focus(row: TreeRow, extend: Boolean = false) {
        focusId = row.layer.base.id
        dispatch(StudioIntent.Select(row.layer.base.id, extend = extend))
        (document.getElementById("studio-layer-${row.layer.base.id}") as? HTMLElement)?.focus()
    }
    Div(attrs = { attr("role", "tree"); attr("aria-label", tr("studio.editor.layers_panel.layers", "Layers")); attr("aria-multiselectable", "true")
        attr("data-studio-region", "layers") }) {
        visible.forEachIndexed { index, row ->
            val layer = row.layer; val id = layer.base.id
            val name = layerDisplayName(layer)
            Div(attrs = {
                id("studio-layer-$id"); attr("role", "treeitem"); tabIndex(if (id == active) 0 else -1)
                attr("aria-level", row.level.toString()); attr("aria-selected", (id in state.selection).toString())
                if (layer is GroupLayer) attr("aria-expanded", (id in expanded).toString())
                attr("aria-label", trf("studio.layer_name", "{name}, {type}, {visibility}, {lock}",
                    "name" to name, "type" to layerTypeName(layer),
                    "visibility" to if (layer.base.hidden) tr("studio.editor.layers_panel.hidden", "hidden") else tr("studio.editor.layers_panel.visible", "visible"),
                    "lock" to if (layer.base.locked) tr("studio.editor.layers_panel.locked", "locked") else tr("studio.editor.layers_panel.unlocked", "unlocked")))
                attr("draggable", (state.editable && layer !is BackgroundLayer && !layer.base.locked).toString())
                style {
                    property("min-height", "40px"); property("padding-inline-start", "${(row.level - 1) * 16}px")
                    property("display", "flex"); property("align-items", "center"); property("gap", Space.SM.toString())
                    property("min-width", "0")
                    if (dropTarget == id) property(if (dropAfter) "border-block-end" else "border-block-start", "2px solid var(--studio-accent)")
                }
                onFocus { focusId = id }
                onClick { if ((it.target as? HTMLElement)?.closest("input, button") == null) dispatch(StudioIntent.Select(id, toggle = it.ctrlKey || it.metaKey, extend = it.shiftKey)) }
                onDoubleClick { if (state.editable && !layer.base.locked && layer !is BackgroundLayer) { renameId = id; rename = layer.base.name } }
                onKeyDown { event ->
                    if (event.isComposing || event.defaultPrevented || (event.target as? HTMLElement)?.closest("button") != null) return@onKeyDown
                    val inField = (event.target as? HTMLElement)?.closest("input, textarea, select, [contenteditable='true']") != null
                    if (inField) {
                        if (event.key == "Escape" || event.key == "Enter") {
                            event.preventDefault(); event.stopPropagation()
                            if (event.key == "Enter") dispatch(StudioIntent.Property(setOf(id), LayerProperty.Name(rename)))
                            renameId = null
                            (document.getElementById("studio-layer-$id") as? HTMLElement)?.focus()
                        }
                        return@onKeyDown
                    }
                    if (event.altKey && event.code.startsWith("Arrow")) return@onKeyDown
                    val command = event.ctrlKey || event.metaKey
                    val next = when (event.key) {
                        "ArrowDown" -> visible.getOrNull(index + 1)
                        "ArrowUp" -> visible.getOrNull(index - 1)
                        "Home" -> visible.firstOrNull()
                        "End" -> visible.lastOrNull()
                        else -> null
                    }
                    if (next != null) { event.preventDefault(); focus(next, event.shiftKey) }
                    else when {
                        event.key == "ArrowRight" && layer is GroupLayer -> {
                            event.preventDefault()
                            if (id !in expanded) expanded = expanded + id else visible.getOrNull(index + 1)?.let { focus(it) }
                        }
                        event.key == "ArrowLeft" -> { event.preventDefault()
                            if (id in expanded) expanded = expanded - id else visible.find { it.layer.base.id == row.parent }?.let { focus(it) }
                        }
                        event.key == "Enter" -> { event.preventDefault(); dispatch(StudioIntent.FocusStage) }
                        event.key == " " -> { event.preventDefault(); dispatch(StudioIntent.Select(id, toggle = true)) }
                        event.key == "F2" && state.editable && !layer.base.locked && layer !is BackgroundLayer -> { event.preventDefault(); renameId = id; rename = layer.base.name }
                        event.key == "Escape" -> { dragging = null; dropTarget = null; renameId = null }
                        event.key in listOf("Delete", "Backspace") && state.editable -> {
                            event.preventDefault(); dispatch(StudioIntent.Select(id))
                            dispatch(StudioIntent.Action(StudioAction(StudioActionType.DELETE)))
                            visible.getOrNull(index + 1)?.let { focus(it) } ?: visible.getOrNull(index - 1)?.let { focus(it) }
                        }
                        command && event.code in listOf("BracketRight", "BracketLeft") && state.editable -> {
                            event.preventDefault(); dispatch(StudioIntent.Reorder(id, if (event.code == "BracketRight") 1 else -1, event.shiftKey))
                        }
                        event.altKey && event.code == "KeyH" && state.editable -> {
                            event.preventDefault(); dispatch(StudioIntent.Property(setOf(id), LayerProperty.Hidden(!layer.base.hidden)))
                        }
                        event.altKey && event.code == "KeyL" && state.editable -> {
                            event.preventDefault(); dispatch(StudioIntent.Property(setOf(id), LayerProperty.Locked(!layer.base.locked)))
                        }
                    }
                }
                ref { element ->
                    val start = org.w3c.dom.events.EventListener { dragging = id }
                    val over = org.w3c.dom.events.EventListener { event ->
                        if (dragging != null && layer !is BackgroundLayer && !layer.base.locked) {
                            event.preventDefault()
                            val mouse = event as org.w3c.dom.events.MouseEvent
                            val rect = element.getBoundingClientRect()
                            val fraction = (mouse.clientY - rect.top) / rect.height
                            dropTarget = if (layer is GroupLayer && fraction in .25.. .75) null else id
                            dropAfter = fraction > .5
                        }
                    }
                    val end = org.w3c.dom.events.EventListener { dragging = null; dropTarget = null }
                    val drop = org.w3c.dom.events.EventListener { event ->
                        val source = dragging
                        if (source != null && source != id && layer !is BackgroundLayer && state.editable) {
                            event.preventDefault()
                            val mouse = event as org.w3c.dom.events.MouseEvent
                            val rect = element.getBoundingClientRect()
                            val fraction = (mouse.clientY - rect.top) / rect.height
                            dispatch(StudioIntent.Drop(source, id, layer is GroupLayer && fraction in .25.. .75, fraction > .5))
                        }
                        dragging = null; dropTarget = null
                    }
                    element.addEventListener("dragend", end)
                    element.addEventListener("dragstart", start); element.addEventListener("dragover", over); element.addEventListener("drop", drop)
                    onDispose { element.removeEventListener("dragend", end); element.removeEventListener("dragstart", start); element.removeEventListener("dragover", over); element.removeEventListener("drop", drop) }
                }
            }) {
                if (renameId == id) LTextField(rename, { rename = it }, label = tr("studio.editor.layers_panel.layer_name", "Layer name"))
                else {
                    Span(attrs = { attr("aria-hidden", "true"); style { property("display", "inline-flex"); property("flex-shrink", "0") } }) {
                        when (layer) {
                            is TextLayer -> LTypeIcon()
                            is DeviceLayer -> LSmartphoneIcon()
                            is ImageLayer -> LImageIcon()
                            is GroupLayer -> LLayersIcon()
                            else -> LSquareIcon()
                        }
                    }
                    Span(attrs = { attr("data-layer-name", ""); style {
                        property("flex", "1"); property("min-width", "0"); property("overflow", "hidden")
                        property("text-overflow", "ellipsis"); property("white-space", "nowrap")
                    } }) { Text(name) }
                }
                if (renameId == id) StudioButton(tr("studio.editor.layers_panel.save_name", "Save name")) {
                    dispatch(StudioIntent.Property(setOf(id), LayerProperty.Name(rename))); renameId = null
                }
                LIconAction(if (layer.base.hidden) trf("studio.show_layer", "Show {layer}", "layer" to name)
                    else trf("studio.hide_layer", "Hide {layer}", "layer" to name),
                    { dispatch(StudioIntent.Property(setOf(id), LayerProperty.Hidden(!layer.base.hidden))) }, modifier = Modifier.tabIndex(-1), enabled = state.editable && layer !is BackgroundLayer) { LEyeIcon() }
                LIconAction(if (layer.base.locked) trf("studio.unlock_layer", "Unlock {layer}", "layer" to name)
                    else trf("studio.lock_layer", "Lock {layer}", "layer" to name),
                    { dispatch(StudioIntent.Property(setOf(id), LayerProperty.Locked(!layer.base.locked))) }, modifier = Modifier.tabIndex(-1), enabled = state.editable && layer !is BackgroundLayer) { LLockIcon() }
            }
        }
    }
}

/** A shared spoken type keeps the layers tree and committed stage summary consistent. */
internal fun layerTypeName(layer: Layer): String = when (layer) {
    is GroupLayer -> tr("studio.editor.layers_panel.group", "group")
    is dev.lingvo.shared.screenshots.doc.TextLayer -> tr("studio.editor.layers_panel.text", "text")
    is dev.lingvo.shared.screenshots.doc.DeviceLayer -> tr("studio.editor.layers_panel.device_frame", "device frame")
    is BackgroundLayer -> tr("studio.editor.layers_panel.background", "background")
    is dev.lingvo.shared.screenshots.doc.ImageLayer -> tr("studio.editor.layers_panel.image", "image")
    is dev.lingvo.shared.screenshots.doc.ShapeLayer -> tr("studio.editor.layers_panel.shape", "shape")
}
